import type { SubscriptionLevel } from '@query';
import { useGetAuthenticatedUser, useGetUserSubscriptionFeatures, useGetUserSubscriptionStatus } from '@query';
import { ROLES_MAP } from '@utils/permissions';

/**
 * Hook for accessing user subscription information and feature access.
 */
export function useSubscription() {
  const { data: subscriptionStatus, isLoading: isLoadingStatus } = useGetUserSubscriptionStatus();
  const { data: features, isLoading: isLoadingFeatures } = useGetUserSubscriptionFeatures();
  const { data: authenticatedUser } = useGetAuthenticatedUser();

  const subscriptionLevel: SubscriptionLevel = subscriptionStatus?.subscriptionLevel || 'FREE';
  const isLoading = isLoadingStatus || isLoadingFeatures;

  // Check if user is admin - admins bypass all subscription limitations
  const isAdmin = authenticatedUser?.roles?.includes(ROLES_MAP.admin) || false;

  // Feature access helpers
  const hasFeatureAccess = (feature: string): boolean => {
    // Admins bypass all subscription limitations
    if (isAdmin) return true;
    return features?.[feature] || subscriptionStatus?.features?.[feature] || false;
  };

  const hasUnlimitedMatches = hasFeatureAccess('unlimited_matches');
  const canJoinGroupHookups = hasFeatureAccess('group_hookups');
  const hasMyNetworkAccess = hasFeatureAccess('my_network');
  const canAccessRecordings = hasFeatureAccess('event_recordings');
  const canHostEvents = hasFeatureAccess('host_events');
  const hasFeedAccess = hasFeatureAccess('feed_access');

  // Subscription level helpers
  const isFree = subscriptionLevel === 'FREE';
  const isPro =
    subscriptionLevel === 'PRO_MONTHLY' || subscriptionLevel === 'PRO_QUARTERLY' || subscriptionLevel === 'PRO_ANNUALLY' || subscriptionLevel === 'KICKSTART';
  const isProMonthly = subscriptionLevel === 'PRO_MONTHLY';
  const isProQuarterly = subscriptionLevel === 'PRO_QUARTERLY';
  const isProAnnually = subscriptionLevel === 'PRO_ANNUALLY';
  const isKickstart = subscriptionLevel === 'KICKSTART';
  const isOnTrial = subscriptionStatus?.onTrial || false; // Always false in new model
  const isTrialExpired = subscriptionStatus?.trialExpired || false; // Always false in new model
  const isExempt = subscriptionStatus?.exempt || false;

  // Access level helpers
  const hasFullAccess = isPro || isExempt || isAdmin;
  const needsUpgrade = isFree && !isExempt && !isAdmin;

  // Match limit information
  const monthlyMatchLimit = subscriptionStatus?.monthlyMatchLimit || 1;
  const hasUnlimitedMatchesAccess = monthlyMatchLimit === Number.MAX_SAFE_INTEGER || hasUnlimitedMatches || isAdmin;

  // Grace period information
  const gracePeriod = subscriptionStatus?.gracePeriod || {
    isInGracePeriod: false,
    daysRemaining: 0,
    endDate: null,
    discountPercentage: 0,
    discountCouponCode: '',
    isEligible: false
  };
  const isInGracePeriod = gracePeriod.isInGracePeriod;
  const gracePeriodDaysRemaining = gracePeriod.daysRemaining;
  const hasGracePeriodDiscount = isInGracePeriod && gracePeriod.discountPercentage > 0;

  return {
    // Subscription status
    subscriptionLevel,
    subscriptionStatus,
    features,
    isLoading,

    // Feature access
    hasFeatureAccess,
    hasUnlimitedMatches,
    canJoinGroupHookups,
    hasMyNetworkAccess,
    canAccessRecordings,
    canHostEvents,
    hasFeedAccess,

    // Subscription level checks
    isFree,
    isPro,
    isProMonthly,
    isProQuarterly,
    isProAnnually,
    isKickstart,
    isOnTrial,
    isTrialExpired,
    isExempt,

    // Access level checks
    hasFullAccess,
    needsUpgrade,
    isAdmin,

    // Match limits
    monthlyMatchLimit,
    hasUnlimitedMatchesAccess,

    // Grace period
    gracePeriod,
    isInGracePeriod,
    gracePeriodDaysRemaining,
    hasGracePeriodDiscount,

    // Utility functions
    getUpgradeMessage: (feature: string) => {
      const messages: Record<string, string> = {
        unlimited_matches: 'Upgrade to PRO to get unlimited 1-to-1 matches instead of 1 per month.',
        group_hookups: 'Upgrade to PRO to join and host Group Hookups.',
        my_network: 'Upgrade to PRO to access My Network feature.',
        event_recordings: 'Upgrade to PRO to access event recordings.',
        host_events: 'Upgrade to PRO to host your own events.'
      };
      return messages[feature] || 'Upgrade to PRO to unlock this feature.';
    },

    // Stripe integration helper
    createCheckoutSession: async (priceId: string, userUuid: string, userEmail: string) => {
      const { stripeService } = await import('@services/stripe');
      return stripeService.createCheckoutSession({
        priceId,
        metadata: { user_uuid: userUuid },
        email: userEmail
      });
    },

    // Trial information
    getTrialDaysRemaining: () => {
      if (!isOnTrial || !subscriptionStatus?.subscriptionEndDate) return 0;
      const endDate = new Date(subscriptionStatus.subscriptionEndDate);
      const now = new Date();
      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    }
  };
}
